import React from "react";

/**
 * Big amount + per-item breakdown in the sidebar.
 *
 * v1 payment links carry ONE line item (amount + description), so
 * the "items" table is really a single row. Leaving the structure
 * item-shaped anyway so future multi-line support doesn't require
 * a component rewrite — just pass an array.
 *
 * `amountDisplay` is mode-agnostic: "$0.50" for USD links, "0.001
 * ETH" for token links. Backend guarantees the shape; we render
 * verbatim. `amountUsd` is the USD face value for stablecoins (1:1)
 * and null for non-stable tokens — only show it when present, and
 * only when it's different from the primary display (i.e. a non-USD
 * stablecoin like DAI would render "0.50 DAI / ≈ $0.50").
 */
export function OrderSummary({ link, tipAmount = 0 }) {
  if (!link) return null;

  const description = link.description || "Payment";
  const showUsdBadge =
    link.amountUsd && !String(link.amountDisplay || "").startsWith("$");
  const totalDisplay = link.amountDisplay; // v1: no tips actually applied to the on-chain amount

  return (
    <>
      <div className="pay-amount">
        <span className="pay-amount__label">Amount due</span>
        <span className="pay-amount__value">{totalDisplay}</span>
        {showUsdBadge && (
          <span className="pay-amount__usd">≈ ${link.amountUsd} USD</span>
        )}
      </div>

      <div className="pay-order">
        <div className="pay-order__row">
          <span className="pay-order__label">{description}</span>
          <span className="pay-order__value">{link.amountDisplay}</span>
        </div>
        {tipAmount > 0 && (
          <div className="pay-order__row">
            <span className="pay-order__label">Tip</span>
            <span className="pay-order__value">{tipAmount}</span>
          </div>
        )}
        {/* Network fee row. x402 "exact" scheme is gasless for the
         * payer — the facilitator fronts the on-chain gas and
         * bills the merchant. We surface $0.00 with a hover hint
         * rather than hide the row entirely so users don't wonder
         * "where's the gas estimate" when they're used to normal
         * dapp UIs. */}
        <div className="pay-order__row pay-order__fee">
          <span className="pay-order__label">
            Network fee{" "}
            <span
              className="pay-fee-hint"
              title="The x402 facilitator pays the on-chain gas fee for you — you only sign the authorization."
              aria-label="The x402 facilitator pays the on-chain gas fee for you."
            >
              ⓘ
            </span>
          </span>
          <span className="pay-order__value">$0.00</span>
        </div>
        <div className="pay-order__row pay-order__row--total">
          <span className="pay-order__label">Total due today</span>
          <span className="pay-order__value">{totalDisplay}</span>
        </div>
      </div>
    </>
  );
}
